import { MinHeap } from '@datastructures-js/heap'
import { loader } from './map_state/loader'
// Relative lumincance used to calculate the cost used in Dijkstra Algorithm
// Cost must be dependent on the brightness of the pixel in map image
export function luminance (r, g, b) {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255
}

// Ctx = canvas context
// w,h = width and height of the image
// eps epsilon used to make sure there is no 0 cost
// gamma works like a threshold or degree to which bright or white pixels should be avoided
// calculates cost map of the whole image using rgba matrix where cost is max when the pixel is transparent / a = 0
// this cost caluclated throguh rgba matrix is then mapped to a 1D array which can be used to access cost later.
// Cost = Min(ε + (relative luminance) ^ γ, maxCost)
export function buildCostMap (ctx, w, h, {
  gamma = 2.2,
  eps = 0.001,
  maxCost = 50
} = {}) {
  const img = ctx.getImageData(0, 0, w, h).data
  const cost = new Float32Array(w * h)

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4
      const r = img[i]; const g = img[i + 1]; const b = img[i + 2]; const a = img[i + 3]

      if (a === 0) {
        cost[y * w + x] = maxCost
        continue
      }

      const br = luminance(r, g, b)
      let c = eps + Math.pow(br, gamma)

      if (c > maxCost) c = maxCost

      cost[y * w + x] = c
    }
  }

  return cost
}

// Async + chunked A* so the loader can paint and the page won't freeze.
// cost = cost 1D map
// w, h = width and height of the map
// start, goal = starting and ending point as selected by the user on canvas
// showLoader / HideLoader = used to indicate that algorithm is being used and its processing
// timeBudgetMs / MaxExpansions = Hard cap on the algorithm so that it does not get stuck and leaves when max count is reached
// turn penality = indicates how much the cost should be increased when algorithm tries to take turn. can be used to keep path straight
// corridor = indicates the grid in which algorithm is allowed to search MinX <= x <= MaxX, MinY <= y <= MaxY
// cost is determined by cost to move 1 pixel in either direction or diagonally + cost by realtive luminance / brightness + turn penality depending upong previous direction
export async function traceAStar (costMap, w, h, start, goal, {
  corridor = null,
  turnPenalty = 0.0,
  timeBudgetMs = 12,
  maxExpansions = 250000
} = {}) {
  loader.setStatusMessage('processing', 'Tracing path…')

  // Let the browser paint the loader before heavy work starts.
  await new Promise(requestAnimationFrame)

  const inBounds = (x, y) =>
    x >= 0 && y >= 0 && x < w && y < h &&
    (!corridor || (
      x >= corridor.minX && x <= corridor.maxX &&
      y >= corridor.minY && y <= corridor.maxY
    ))

  const sx = start.x | 0; const sy = start.y | 0
  const gx = goal.x | 0; const gy = goal.y | 0

  const N = w * h

  const gScore = new Float32Array(N)
  gScore.fill(Infinity)

  const cameFrom = new Int32Array(N)
  cameFrom.fill(-1)

  const cameDir = new Int8Array(N)
  cameDir.fill(127)

  const heap = new MinHeap((item) => item.f)

  const sI = idx(sx, sy, w)
  gScore[sI] = 0
  heap.insert({ x: sx, y: sy, f: Math.hypot(gx - sx, gy - sy) })

  const dirs = [
    { dx: 1, dy: 0, ind: 0, length: 1 },
    { dx: 1, dy: 1, ind: 1, length: Math.SQRT2 },
    { dx: 0, dy: 1, ind: 2, length: 1 },
    { dx: -1, dy: 1, ind: 3, length: Math.SQRT2 },
    { dx: -1, dy: 0, ind: 4, length: 1 },
    { dx: -1, dy: -1, ind: 5, length: Math.SQRT2 },
    { dx: 0, dy: -1, ind: 6, length: 1 },
    { dx: 1, dy: -1, ind: 7, length: Math.SQRT2 }
  ]

  const reconstruct = (endIndex) => {
    const path = []
    let p = endIndex
    while (p !== -1) {
      const y = (p / w) | 0
      const x = p - y * w
      path.push({ x, y })
      p = cameFrom[p]
    }
    path.reverse()
    return path
  }

  let expansions = 0

  try {
    let lastUiUpdate = 0
    while (heap.size() > 0) {
      const sliceStart = performance.now()

      while (heap.size() > 0 && (performance.now() - sliceStart) < timeBudgetMs) {
        if (++expansions > maxExpansions) return null

        const t = performance.now()
        if (t - lastUiUpdate > 50) {
          loader.setStatusMessage('processing', `Tracing path… ${expansions.toLocaleString()} nodes (slice ${Math.round(t - sliceStart)}ms)`)
          lastUiUpdate = t
        }
        const cur = heap.extractRoot()
        const cx = cur.x; const cy = cur.y
        const cI = idx(cx, cy, w)

        if (cx === gx && cy === gy) {
          return reconstruct(cI)
        }

        for (const direction of dirs) {
          const nx = cx + direction.dx
          const ny = cy + direction.dy
          if (!inBounds(nx, ny)) continue

          const nI = idx(nx, ny, w)

          let tentative = gScore[cI] + direction.length + costMap[nI]

          if (turnPenalty > 0) {
            const prevD = cameDir[cI]
            if (prevD !== 127) {
              const diff = Math.abs(direction.ind - prevD)
              const turn = Math.min(diff, 8 - diff)
              tentative += turnPenalty * turn
            }
          }

          if (tentative < gScore[nI]) {
            cameFrom[nI] = cI
            cameDir[nI] = direction.ind
            gScore[nI] = tentative

            const hCost = Math.hypot(gx - nx, gy - ny)
            heap.insert({ x: nx, y: ny, f: tentative + hCost })
          }
        }
      }

      await new Promise(requestAnimationFrame)
    }

    return null
  } finally {
    loader.setStatusMessage('success', 'Tracing complete')
  }
}

// used for calculating the length in pixel for the traced shortest path uses pythagoras theorem as all points are aligned at right angles
export function pathLengthPx (path) {
  let len = 0
  for (let i = 0; i < path.length - 1; i++) {
    len += Math.hypot(path[i + 1].x - path[i].x, path[i + 1].y - path[i].y)
  }
  return len
}

// Bresenham Line is used to calculate the path betwen two points necessary to remove the jaggedness of the shrotest path obtained by A*
// Each point is taken from traced A* path using binary search n, n/2, => n/4 / 3n/4, ... which is then used to calculate line which
// might be jagged but it can be made smaller by directly connecting end points
export function bresenhamLine (x0, y0, x1, y1) {
  x0 |= 0; y0 |= 0; x1 |= 0; y1 |= 0

  const pts = []
  const dx = Math.abs(x1 - x0); const sx = x0 < x1 ? 1 : -1
  const dy = -Math.abs(y1 - y0); const sy = y0 < y1 ? 1 : -1
  let err = dx + dy

  while (true) {
    pts.push({ x: x0, y: y0 })
    if (x0 === x1 && y0 === y1) break
    const e2 = err * 2
    if (e2 >= dy) { err += dy; x0 += sx }
    if (e2 <= dx) { err += dx; y0 += sy }
  }
  return pts
}

// need to get proper inded form build cost map and to check if the index lies within bounds
function idx (x, y, w) { return y * w + x }
function inBounds (x, y, w, h) { return x >= 0 && y >= 0 && x < w && y < h }

// used to calculate the mean cost, bad point ratio and max bright / cost point of the actual A* path and BressenHAM line
// which is then used to compare the actual traced A* and Bresenham(B) line if total cost of B line < A* line then
// prefer B line otherwise leave A* line points as it is.
function statsFromSamples (samples, badThreshold = Infinity) {
  let sum = 0
  let max = -Infinity
  let bad = 0

  const n = samples.length || 1
  for (let i = 0; i < samples.length; i++) {
    const v = samples[i]
    sum += v
    if (v > max) max = v
    if (v > badThreshold) bad++
  }

  return {
    mean: sum / n,
    max,
    badRatio: bad / n
  }
}

// used to calculate the Bressenham line cost which is used to compare with A* line
function costsAlongLine (a, b, costMap, w, h) {
  const pts = bresenhamLine(a.x, a.y, b.x, b.y)
  const vals = []
  for (let i = 0; i < pts.length; i++) {
    const { x, y } = pts[i]
    if (!inBounds(x, y, w, h)) return null // out-of-bounds => invalid
    vals.push(costMap[idx(x, y, w)])
  }
  return vals
}

// used to calculate the A* line cost which is used to compare with Bressenham line
function costsAlongJagged (path, i, j, costMap, w, h) {
  const vals = []
  for (let k = i; k <= j; k++) {
    const { x, y } = path[k]
    if (!inBounds(x, y, w, h)) return null
    vals.push(costMap[idx(x, y, w)])
  }
  return vals
}

// Compares Bressenham line and A* line to see the new shortcut path has less cost compared to A*
// or not. returns the points / path depending upon the line with minimum cost
function isShortcutOK (path, i, j, costMap, w, h, opts) {
  const a = path[i]; const b = path[j]

  const JaggedLineCost = costsAlongJagged(path, i, j, costMap, w, h)
  if (!JaggedLineCost) return false

  const jStats = statsFromSamples(JaggedLineCost)
  const badThreshold = jStats.max

  const LineCost = costsAlongLine(a, b, costMap, w, h)
  if (!LineCost) return false

  const sStats = statsFromSamples(LineCost, badThreshold)

  if (sStats.mean > jStats.mean * (1 + opts.tolMean)) return false

  if (sStats.badRatio > opts.tolBadRatio) return false

  if (opts.hardMaxCost != null && sStats.max > opts.hardMaxCost) return false

  return true
}

// finds farthest valid path that does not cross bright pixels upto a threshold
// uses binary search to keep the time cost of the shortest path calculation as low as possible
function farthestValidFrom (path, i, costMap, w, h, opts) {
  let lo = i + 1
  let hi = path.length - 1
  let best = i + 1

  // If even next point is invalid (shouldn't happen), just return i+1
  if (lo >= path.length) return i
  if (!isShortcutOK(path, i, lo, costMap, w, h, opts)) return lo

  // Binary search for farthest "likely" valid
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (isShortcutOK(path, i, mid, costMap, w, h, opts)) {
      best = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }

  const start = Math.max(i + 1, best - opts.localScan)
  const end = Math.min(path.length - 1, best + opts.localScan)

  let best2 = best
  for (let j = best; j <= end; j++) {
    if (isShortcutOK(path, i, j, costMap, w, h, opts)) best2 = j
    else break
  }

  for (let j = best2; j >= start; j--) {
    if (isShortcutOK(path, i, j, costMap, w, h, opts)) return j
  }

  return best2
}

// uses all above mentioned functions to calculate the shortcut path. pelase see the details on each function
export function shortcutPathLOS (path, costMap, width, height, options = {}) {
  if (!path || path.length < 2) return path || []

  const opts = {
    tolMean: 0.05,
    tolBadRatio: 0.02,
    hardMaxCost: null,
    localScan: 20,
    ...options
  }

  const out = []
  let i = 0
  out.push(path[0])

  while (i < path.length - 1) {
    const j = farthestValidFrom(path, i, costMap, width, height, opts)
    out.push(path[j])
    i = j
  }

  return out
}

// used to remove jaggedness further by using angle if its in threshold then remove the excess point
export function pruneByAngle (path, deg = 5) {
  if (path.length < 3) return path
  const out = [path[0]]
  const cosThresh = Math.cos((deg * Math.PI) / 180)

  for (let i = 1; i < path.length - 1; i++) {
    const a = out[out.length - 1]
    const b = path[i]
    const c = path[i + 1]

    const v1x = b.x - a.x; const v1y = b.y - a.y
    const v2x = c.x - b.x; const v2y = c.y - b.y

    const d1 = Math.hypot(v1x, v1y)
    const d2 = Math.hypot(v2x, v2y)
    if (d1 === 0 || d2 === 0) continue

    const cos = (v1x * v2x + v1y * v2y) / (d1 * d2)

    if (cos > cosThresh) continue

    out.push(b)
  }
  out.push(path[path.length - 1])
  return out
}

export function removeJaggedness (path, costMap, w, h, reps = 3, opts = {}) {
  loader.setStatusMessage('processing', 'Smoothing traced path…')
  if (!path || path.length < 3) return path || []
  reps = Math.max(1, reps | 0)

  let p = path
  for (let i = 0; i < reps; i++) {
    p = shortcutPathLOS(p, costMap, w, h, opts)
    if (p.length < 3) break
  }

  p = pruneByAngle(p, 8)
  loader.setStatusMessage('success', 'Smoothing complete.')
  return p
}
